import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import * as XLSX from 'xlsx';

// Define the shape of our data context
interface DataContextType {
    officers: any[];
    services: any[];
    tenders: any[];
    news: any[];
    documents: any[];
    stats: any[];
    departments: any[];
    schemes: any[];
    acts: any[];
    // New fields
    pmQuote: any | null;
    socialLinks: any | null;
    generalInfo: any | null;
    emergencyContacts: any[];
    governmentPortals: any[];
    aboutQuickLinks: any[];
    recentDocCategories: any[];
    wards: any[];
    vacancies: any[];
    sops: any[];
    publicInfoItems: any[]; // Alias for documents, used by GlobalSearch
    gallery: any[];

    loading: boolean;
    lastUpdated: Date | null;
    refreshData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

// Initial empty state
const INITIAL_STATE = {
    officers: [],
    services: [],
    tenders: [],
    news: [],
    documents: [],
    stats: [],
    departments: [],
    schemes: [],
    acts: [],
    // New fields
    pmQuote: null,
    socialLinks: null,
    generalInfo: null,
    emergencyContacts: [],
    governmentPortals: [],
    aboutQuickLinks: [],
    recentDocCategories: [],
    wards: [],
    vacancies: [],
    sops: [],
    publicInfoItems: [],
    gallery: [],

    loading: true,
    lastUpdated: null,
    refreshData: async () => { },
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [data, setData] = useState<Omit<DataContextType, 'loading' | 'lastUpdated' | 'refreshData'>>(INITIAL_STATE);
    const [loading, setLoading] = useState(true);
    const [lastModifiedHeader, setLastModifiedHeader] = useState<string | null>(null);
    const [docsLastModifiedHeader, setDocsLastModifiedHeader] = useState<string | null>(null);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const loadData = useCallback(async (isPolling = false) => {
        try {
            // Step 1: Check if files have changed using HEAD request
            const [dataHead, docsHead] = await Promise.all([
                fetch('/site-data.xlsx', { method: 'HEAD', cache: 'no-cache' }),
                fetch('/documents.json', { method: 'HEAD', cache: 'no-cache' })
            ]);

            if (!dataHead.ok) throw new Error('Failed to access data file');

            const newLastModified = dataHead.headers.get('Last-Modified');
            const newDocsLastModified = docsHead.ok ? docsHead.headers.get('Last-Modified') : null;

            // If polling and timestamp hasn't changed, do nothing
            // For manual refresh (isPolling=false), we bypass this check if needed, 
            // but relying on Last-Modified is usually good practice. 
            // However, to force update despite cache, we might want to proceed.
            // If the user wants to "check any update", we should fetch.

            if (isPolling && lastModifiedHeader && newLastModified === lastModifiedHeader &&
                ((!newDocsLastModified) || (docsLastModifiedHeader && newDocsLastModified === docsLastModifiedHeader))) {
                return;
            }

            // Step 2: Fetch the full file
            // Add timestamp to url to prevent caching
            const response = await fetch(`/site-data.xlsx?t=${Date.now()}`);
            const arrayBuffer = await response.arrayBuffer();

            // Step 3: Parse Excel
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });

            const parseSheet = (sheetName: string) => {
                const sheet = workbook.Sheets[sheetName];
                return sheet ? XLSX.utils.sheet_to_json(sheet) : [];
            };

            // Parse General Info
            const generalInfoRaw = parseSheet('GeneralInfo');

            // Collect key_achievements and future_goals from columns C and D
            const keyAchievements: string[] = [];
            const futureGoals: string[] = [];

            const generalInfo: any = generalInfoRaw.reduce((acc: any, item: any) => {
                // Parse key-value pairs from columns A and B
                if (item.key && item.value) {
                    acc[item.key] = item.value;
                }
                // Collect Key Achievements from column C
                if (item['Key Achievements']) {
                    keyAchievements.push(item['Key Achievements']);
                }
                // Collect Future Goals from column D
                if (item['Future Goals']) {
                    futureGoals.push(item['Future Goals']);
                }
                return acc;
            }, {}); // No fallbacks - only show data from Excel

            // Add arrays to generalInfo
            if (keyAchievements.length > 0) {
                generalInfo.key_achievements_list = keyAchievements;
            }
            if (futureGoals.length > 0) {
                generalInfo.future_goals_list = futureGoals;
            }

            // Fetch local document index (Keep as fallback or secondary source if needed, but per user plan we use Excel)
            let documentsIndex = [];
            try {
                // We verify documents.json too just in case
                const docResponse = await fetch('/documents.json?t=' + Date.now());
                if (docResponse.ok) {
                    documentsIndex = await docResponse.json();
                }
            } catch (e) {
                console.warn('Failed to load documents.json index', e);
            }

            const socialData = parseSheet('SocialLinks');
            const socialLinks = socialData.length > 0 ? socialData[0] : null;

            const rawPeople = parseSheet('PEOPLES');

            // 1. Filter by Visibility (Strict: Show unless is_visible is explicitly FALSE)
            // 1. Filter by Visibility (Strict: Show unless is_visible is explicitly FALSE)
            const visiblePeople = rawPeople.filter((p: any) => {
                const vis = String(p.is_visible).trim().toLowerCase();
                return vis !== 'false' && vis !== 'no' && vis !== '0';
            });

            // 2. Process People (Officers, staff, etc.)
            const processedOfficers = visiblePeople.map((p: any) => {
                let role = p.role_type ? p.role_type.toLowerCase() : '';
                const des = (p.designation || '').toUpperCase();

                // Fallback role detection if empty
                if (!role) {
                    if (des.includes('PRESIDENT') && !des.includes('OF INDIA')) role = 'municipal';
                    else if (des.includes('EXECUTIVE OFFICER') || des === 'EO' || des.includes('E.O')) {
                        role = 'municipal';
                        // Normalize designation for consistency in filters
                        p.designation = "Executive Officer";
                    }
                    else if (des.includes('SECRETARY') && !des.includes('UNDER')) role = 'municipal';
                    else if (des.includes('MUNICIPAL ENGINEER') || des === 'ME') role = 'municipal';
                    else if (des === 'MEMBER' || p.ward_number || p['Ward INCHARGE']) role = 'municipal';
                    else role = 'officer'; // Default fallback
                }

                // Normalizing keys
                return {
                    ...p,
                    id: p.id || Math.random().toString(36).substr(2, 9),
                    role_type: role,
                    ward_number: [p.ward_number, p.ward_no, p['Ward INCHARGE'], p['ward no'], p['Ward No']].find(v => v !== undefined && v !== null && v !== ''),
                    map_url: p.map_url || p['google map link'], // Support aliases
                    quote: p.quote,
                    branch: p.branch,
                    branch_label: p.branch_label,
                    is_incharge: p.is_incharge === true || String(p.is_incharge).toUpperCase() === 'TRUE' || String(p.is_incharge).toLowerCase() === 'yes',
                    rank: p.rank ? parseInt(p.rank) : 3 // Default rank 3 for normal members
                };
            });

            // Derive PM Quote from National Leaders or anyone with a quote
            // We look for a 'national' role first with a quote, or fallback to the first person with a quote.
            const quotePerson = processedOfficers.find((p: any) => p.quote && p.role_type === 'national')
                || processedOfficers.find((p: any) => p.quote);

            const pmQuote = quotePerson ? {
                quote: quotePerson.quote,
                name: quotePerson.name,
                designation: quotePerson.designation,
                image: quotePerson.image || quotePerson.image_url
            } : null;

            // 3. Derive Wards from the processed list
            // Create a ward entry for ANYONE with a ward_number (0 = President/VP)
            const derivedWards = processedOfficers
                .filter((p: any) => p.ward_number !== undefined && p.ward_number !== null && String(p.ward_number).trim() !== '')
                .map((p: any) => {
                    const wn = String(p.ward_number).trim();
                    const des = (p.designation || 'MEMBER').toUpperCase();

                    let rank = 3;
                    let isLeadership = false;

                    // Ward 0/00 OR Designation 'President'/'Vice President' (among ward holders) indicates Leadership
                    if (wn === '0' || wn === '00' || des.includes('PRESIDENT')) {
                        isLeadership = true;
                        if (des.includes('PRESIDENT') && !des.includes('VICE')) rank = 1;
                        else if (des.includes('VICE PRESIDENT')) rank = 2;
                    }

                    return {
                        id: `ward-${wn}`,
                        number: isLeadership ? '-' : wn,
                        name: isLeadership ? (rank === 1 ? 'President' : 'Vice President') : `Ward ${wn}`,
                        councillor: p.name,
                        designation: des === 'MEMBER' ? 'MEMBER' : des,
                        areas: [], // Can be populated if needed
                        contact_number: p.phone,
                        population: p.population,
                        image_url: p.image_url,
                        map_url: p.map_url || p['google map link'],
                        rank: rank
                    };
                })
                .sort((a: any, b: any) => {
                    if (a.rank !== b.rank) return a.rank - b.rank;
                    const numA = parseInt(a.number) || 999;
                    const numB = parseInt(b.number) || 999;
                    return numA - numB;
                });

            // DOCUMENTS: Merge strategy
            const excelDocuments = parseSheet('Documents');
            const localDocsIndex = documentsIndex;
            const mergedDocumentsMap = new Map();

            const normalizePath = (p: string) => {
                if (!p) return '';
                let pathStr = p.trim().replace(/\\/g, '/');
                if (!pathStr.startsWith('/')) pathStr = '/' + pathStr;
                return pathStr.toLowerCase();
            };

            const excelMap = new Map();
            excelDocuments.forEach((doc: any) => {
                const link = doc.link || doc.url;
                if (link) {
                    excelMap.set(normalizePath(link), doc);
                }
            });

            const processedLocalPaths = new Set<string>();
            localDocsIndex.forEach((localDoc: any) => {
                const path = normalizePath(localDoc.url || localDoc.link);
                processedLocalPaths.add(path);
                const excelMatch = excelMap.get(path);

                if (excelMatch) {
                    mergedDocumentsMap.set(path, {
                        ...localDoc,
                        ...excelMatch,
                        link: localDoc.url || localDoc.link,
                        date: localDoc.date || excelMatch.date,
                    });
                } else {
                    mergedDocumentsMap.set(path, {
                        ...localDoc,
                        link: localDoc.url || localDoc.link,
                        category: localDoc.category || 'General',
                        title: localDoc.title || localDoc.name
                    });
                }
            });

            excelMap.forEach((doc: any, key: string) => {
                if (processedLocalPaths.has(key)) return;
                mergedDocumentsMap.set(key, doc);
            });

            const finalDocuments = Array.from(mergedDocumentsMap.values());

            // Derive Branches from Officers
            const branchesMap = new Map<string, any>();

            // Helper to assign icons based on branch name (heuristic)
            const getBranchIcon = (name: string): string => {
                const n = name.toLowerCase();
                if (n.includes('account') || n.includes('finance')) return 'Calculator';
                if (n.includes('audit')) return 'FileCheck';
                if (n.includes('birth') || n.includes('death') || n.includes('health')) return 'Activity';
                if (n.includes('building') || n.includes('construction') || n.includes('work')) return 'HardHat';
                if (n.includes('diary') || n.includes('dispatch') || n.includes('record')) return 'ClipboardList';
                if (n.includes('engineer') || n.includes('mechanic')) return 'Wrench';
                if (n.includes('gis') || n.includes('map') || n.includes('survey')) return 'Map';
                if (n.includes('it cell') || n.includes('computer') || n.includes('tech')) return 'Monitor';
                if (n.includes('legal') || n.includes('law') || n.includes('court')) return 'Scale';
                if (n.includes('nulm') || n.includes('livelihood') || n.includes('skill')) return 'Briefcase';
                if (n.includes('pmay') || n.includes('housing') || n.includes('colony')) return 'Home';
                if (n.includes('rent') || n.includes('estate') || n.includes('property')) return 'Key';
                if (n.includes('tax') || n.includes('revenue')) return 'Banknote';
                if (n.includes('sanitation') || n.includes('waste') || n.includes('clean')) return 'Trash2';
                if (n.includes('light') || n.includes('electric')) return 'Lightbulb';
                if (n.includes('fire')) return 'Flame';
                if (n.includes('establish') || n.includes('admin') || n.includes('hr')) return 'Users';
                return 'Building2'; // Default
            };

            processedOfficers.forEach((p: any) => {
                if (!p.branch) return;

                // Split branches by comma to support multi-branch assignment (e.g., "Rent, Marriage")
                const rawBranches = String(p.branch).split(',');

                rawBranches.forEach(rawBranch => {
                    const branchName = rawBranch.trim();
                    if (!branchName) return;

                    if (!branchesMap.has(branchName)) {
                        branchesMap.set(branchName, {
                            id: `branch-${branchName.replace(/\s+/g, '-').toLowerCase()}`,
                            name: branchName,
                            description: `Oversees all ${branchName} related operations and municipal services.`,
                            incharge: null,
                            inchargeDesignation: null,
                            icon: getBranchIcon(branchName),
                            pdf_url: null,
                            pdf_description: null,
                            employees: []
                        });
                    }

                    const branch = branchesMap.get(branchName);

                    // If person is in charge, set them along with their designation.
                    if (p.is_incharge && !branch.incharge) {
                        branch.incharge = p.name;
                        branch.inchargeDesignation = p.designation || null;
                    }

                    // Add to employees list (excluding incharge if desired, but user asked for members info)
                    // For now, let's add everyone to employees list so they show up as team members.
                    // If is_incharge is true, they are already shown as In-charge, so maybe we exclude them? 
                    // Usually "Team Members" implies everyone or everyone else. Let's include everyone for completeness or exclude in-charge from list.
                    // Let's exclude the in-charge person from the 'Team Members' list to avoid duplication if the modal shows In-charge separately.
                    // However, we only set incharge if it was null. If multiple in-charges, subsequent ones should go to list.

                    const isCurrentPersonInCharge = p.is_incharge && branch.incharge === p.name;

                    if (!isCurrentPersonInCharge) {
                        if (!branch.employees) branch.employees = [];
                        branch.employees.push(`${p.name} (${p.designation || 'Member'})`);
                    }
                });
            });

            // Parse BRANCHES sheet for custom descriptions and documents
            const branchesSheetData = parseSheet('BRANCHES');
            branchesSheetData.forEach((b: any) => {
                const branchName = b.name || b.Name || b.branch || b.Branch;
                if (!branchName) return;

                // Create branch if it doesn't exist from PEOPLES data
                if (!branchesMap.has(branchName)) {
                    branchesMap.set(branchName, {
                        id: `branch-${branchName.replace(/\s+/g, '-').toLowerCase()}`,
                        name: branchName,
                        description: b.description || b.Description || `Oversees all ${branchName} related operations and municipal services.`,
                        incharge: null,
                        inchargeDesignation: null,
                        icon: b.icon || getBranchIcon(branchName),
                        pdf_url: b.pdf_url || b.document_url || null,
                        pdf_description: b.pdf_description || b.document_name || null
                    });
                } else {
                    // Merge custom data into existing branch
                    const existing = branchesMap.get(branchName);
                    if (b.description || b.Description) {
                        existing.description = b.description || b.Description;
                    }
                    if (b.pdf_url || b.document_url) {
                        existing.pdf_url = b.pdf_url || b.document_url;
                    }
                    if (b.pdf_description || b.document_name) {
                        existing.pdf_description = b.pdf_description || b.document_name;
                    }
                    if (b.icon) {
                        existing.icon = b.icon;
                    }
                }
            });

            const derivedDepartments = Array.from(branchesMap.values()).sort((a: any, b: any) => a.name.localeCompare(b.name));



            // Fetch generated news from file system (if any)
            let generatedNews = [];
            try {
                const newsRes = await fetch('/generated/news.json?t=' + Date.now());
                if (newsRes.ok) {
                    generatedNews = await newsRes.json();
                }
            } catch (e) {
                console.log('No generated news found');
            }

            const excelNews = parseSheet('News');

            // Merge news: Excel info + Generated info
            // We can treat them as a combined list.
            const finalNews = [...excelNews, ...generatedNews].sort((a: any, b: any) => {
                // Sort by date descending
                return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
            });

            setData({
                officers: processedOfficers,
                services: parseSheet('Services'),
                tenders: parseSheet('Tenders'),
                news: finalNews,
                documents: finalDocuments,
                stats: parseSheet('Stats'),
                departments: derivedDepartments,
                schemes: parseSheet('Schemes'),
                acts: parseSheet('Acts'),
                pmQuote,
                socialLinks,
                generalInfo,
                emergencyContacts: parseSheet('EmergencyContacts'),
                governmentPortals: parseSheet('GovernmentPortals'),
                aboutQuickLinks: parseSheet('AboutQuickLinks'),
                // Recalculate categories from the final document list
                recentDocCategories: Object.values(finalDocuments.reduce((acc: any, doc: any) => {
                    const cat = doc.category || 'General';
                    if (!acc[cat]) {
                        acc[cat] = {
                            id: cat,
                            title: cat,
                            icon: 'FileText',
                            path: `/documents/${cat.toLowerCase()}`,
                            items: []
                        };
                    }
                    acc[cat].items.push(doc);
                    return acc;
                }, {})),
                wards: derivedWards,
                vacancies: parseSheet('Vacancies'),
                sops: parseSheet('SOPs'),
                publicInfoItems: finalDocuments,
                gallery: parseSheet('Gallery'),
            });

            setLastModifiedHeader(newLastModified);
            setDocsLastModifiedHeader(newDocsLastModified);
            setLastFetched(new Date());
        } catch (error) {
            console.error('Error loading site data:', error);
        } finally {
            if (!isPolling) setLoading(false);
        }
    }, [lastModifiedHeader, docsLastModifiedHeader]);

    useEffect(() => {
        loadData(false);
        const interval = setInterval(() => {
            loadData(true);
        }, 30000);
        return () => clearInterval(interval);
    }, [loadData]);

    const refreshData = async () => {
        setLoading(true);
        await loadData(false); // Force reload
    };

    return (
        <DataContext.Provider value={{ ...data, loading, lastUpdated: lastFetched, refreshData }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
